import * as React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import OutboxRoundedIcon from '@mui/icons-material/OutboxRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { AdminGeneralInfoResult } from '../../../../models/adminGeneral';
import { formatRubles } from '../../../../common/money';
import { Delta } from '../Delta';
import { useNavigate } from 'react-router-dom';

export type WithdrawalsCardProps = { data: AdminGeneralInfoResult | null };

export const WithdrawalsCard: React.FC<WithdrawalsCardProps> = React.memo(({ data }) => {
    const navigate = useNavigate();
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/app/payment-methods#stats-charts')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/payment-methods#stats-charts'); } }}
            >
                <OutboxRoundedIcon fontSize="small" />
                <Typography level="title-md">Выводы</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="body-sm">Количество</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Количество выводов за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.withdrawalCount ?? 0) > (data.withdrawalCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${data.withdrawalCount ?? 0}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${data.withdrawalCountLastPeriod ?? 0})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.withdrawalCount ?? 0} previous={data.withdrawalCountLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">Сумма</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Сумма выводов за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.withdrawalSum ?? 0) > (data.withdrawalSumLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.withdrawalSum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.withdrawalSumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.withdrawalSum ?? 0} previous={data.withdrawalSumLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box sx={{ flexBasis: '100%', width: '100%', mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', columnGap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap', width: '100%' }}>
                        <Tooltip title="Профит по выводам за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                                <TrendingUpRoundedIcon fontSize="small" sx={{ color: (data && (data.withdrawalProfit ?? 0) > (data.withdrawalProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }} />
                                <Typography noWrap level="title-lg" sx={{ color: (data && (data.withdrawalProfit ?? 0) > (data.withdrawalProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.withdrawalProfit ?? 0)}` : '-'}
                                </Typography>
                                <Typography noWrap level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.withdrawalProfitLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.withdrawalProfit ?? 0} previous={data.withdrawalProfitLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (кол-во)</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Среднее кол-во выводов в день этого (прошлого) периодов" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.withdrawalInDayCount ?? 0) > (data.withdrawalInDayCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${data.withdrawalInDayCount ?? 0}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${data.withdrawalInDayCountLastPeriod ?? 0})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.withdrawalInDayCount ?? 0} previous={data.withdrawalInDayCountLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (сумма)</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Средняя сумма выводов в день этого (прошлого) периодов" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.withdrawalInDaySum ?? 0) > (data.withdrawalInDaySumLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.withdrawalInDaySum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.withdrawalInDaySumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.withdrawalInDaySum ?? 0} previous={data.withdrawalInDaySumLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
            </Box>
        </Sheet>
    );
});

WithdrawalsCard.displayName = 'WithdrawalsCard';


